import { get } from "lodash-es"

// Define the possible opacity steps and their corresponding Tailwind classes
const opacityMapping: { [key: number]: number } = {
  0: 0,
  5: 5,
  10: 10,
  20: 20,
  25: 25,
  30: 30,
  40: 40,
  50: 50,
  60: 60,
  70: 70,
  75: 75,
  80: 80,
  90: 90,
  95: 95,
  100: 100,
}

interface BlurSettings {
  overlayColor: string
  overlayOpacity: string
  blurIntensity: string
}

type BlurKeys = Record<string, string>

type SetBlurValues = (
  key: string,
  overlayColor: string,
  overlayOpacity: string,
  blurIntensity: string,
) => void

type ArgsProps = Record<string, any>

// Function to map numeric opacity to closest allowed Tailwind class
export const mapOverlayOpacity = (value: number): string => {
  const keys = Object.keys(opacityMapping).map(Number)

  // If the value is less than the smallest allowed, map to the smallest
  if (value < keys[0]) return value.toString()

  // If the value is greater than the largest allowed, map to the largest
  if (value > keys[keys.length - 1]) return keys[keys.length - 1].toString()

  // Find the closest valid value
  let closest = keys[0]
  let minDiff = Math.abs(value - closest)
  for (let i = 1; i < keys.length; i++) {
    const diff = Math.abs(value - keys[i])
    if (diff < minDiff) {
      closest = keys[i]
      minDiff = diff
    }
  }
  return closest.toString()
}

// Function to resolve overlay color
export const resolveOverlayColor = (value: string): string => {
  switch (value) {
    case "light":
      return "bg-white"
    case "dark":
      return "bg-black"
    default:
      return value // Assume valid Tailwind class
  }
}

// Initialize default blur props
export const generateBlurProps = (blurKeys: BlurKeys): Record<string, any> => {
  const defaultProps: Record<string, any> = {}
  Object.keys(blurKeys).forEach((key) => {
    defaultProps[`${key}-overlayColor`] = null
    defaultProps[`${key}-overlayOpacity`] = -1 // Store as string
    defaultProps[`${key}-blurIntensity`] = null
  })
  return defaultProps
}

// Generate argument types for Storybook controls
export const generateArgTypes = (blurKeys: BlurKeys): Record<string, any> => {
  const argTypes: Record<string, any> = {}

  Object.keys(blurKeys).forEach((key) => {
    // Overlay color dropdown
    argTypes[`${key}-overlayColor`] = {
      control: "select",
      options: ["light", "dark", "bg-transparent"], // Add more as needed
      defaultValue: "light",
    }

    // Overlay opacity range (controller provides number)
    argTypes[`${key}-overlayOpacity`] = {
      control: {
        type: "range",
        min: 0,
        max: 100,
        step: 5,
      },
      defaultValue: 30,
    }

    // Blur intensity dropdown
    argTypes[`${key}-blurIntensity`] = {
      control: "select",
      options: ["none", "sm", "md", "lg", "xl"],
      defaultValue: "md",
    }
  })

  return argTypes
}

// Extract blur props for a given key
export const getBlurProps = (
  props: ArgsProps,
  blurKey: string,
): BlurSettings => {
  return {
    overlayColor: resolveOverlayColor(
      get(props, `${blurKey}-overlayColor`, "light"),
    ),
    overlayOpacity: mapOverlayOpacity(
      Number(get(props, `${blurKey}-overlayOpacity`, 30)),
    ), // Convert to string mapped value
    blurIntensity: get(props, `${blurKey}-blurIntensity`, "backdrop-blur-md"),
  }
}

// Update context state based on args
export const updateBlurContextFromArgs = (
  args: ArgsProps | undefined,
  setBlurValues: SetBlurValues,
  blurKeys: BlurKeys,
): void => {
  Object.keys(blurKeys).forEach((key) => {
    const overlayColorKey = `${key}-overlayColor`
    const overlayOpacityKey = `${key}-overlayOpacity`
    const blurIntensityKey = `${key}-blurIntensity`

    const overlayColorArg: string =
      args[overlayColorKey] !== undefined
        ? resolveOverlayColor(args[overlayColorKey])
        : "bg-white"

    const overlayOpacityArg: string =
      args[overlayOpacityKey] !== undefined
        ? mapOverlayOpacity(Number(args[overlayOpacityKey]))
        : "30"

    const blurIntensityArg: string =
      args[blurIntensityKey] !== undefined ? args[blurIntensityKey] : "md"

    setBlurValues(
      blurKeys[key],
      overlayColorArg,
      overlayOpacityArg,
      blurIntensityArg,
    )
  })
}
